import { z } from 'zod';
import { SPOT_KEYS } from '../core/spots';
import { IdZ, TsZ } from './common';
import { RatingDeltaZ } from './rating';

/**
 * Drill sessions and attempts (R10, R20). One attempt row per shot: attempted is always 1,
 * made is 0 or 1, so dots and streaks work. Combine levels in v1: The Arc, Midrange Tour,
 * Layup Line, Charity Stripe (the core four) and Iso Gauntlet (real 1v1 games to 3 linked by
 * games.drill_session_id, no attempts).
 */
export const COMBINE_LEVELS = ['arc', 'midrange', 'layup', 'charity', 'iso'] as const;
export const CombineLevelZ = z.enum(COMBINE_LEVELS);
export type CombineLevel = z.infer<typeof CombineLevelZ>;
export const CORE_COMBINE_LEVELS = ['arc', 'midrange', 'layup', 'charity'] as const satisfies readonly CombineLevel[];

/**
 * Shots per player that make a core level "finished" (docs/CONTRACTS.md, "Combine done").
 * A level is done for a player at time T when a final, non-deleted drill session of that
 * level holds at least this many non-voided attempts by that player, recorded at or before T.
 * Combine's catalog (DrillLevelInfo.shots) must equal these numbers.
 */
export const CORE_LEVEL_SHOTS = { arc: 25, midrange: 25, layup: 10, charity: 10 } as const satisfies Record<
  (typeof CORE_COMBINE_LEVELS)[number],
  number
>;

export const DRILL_PROGRAMS = ['combine_v1', 'shootaround'] as const;
export const DrillProgramZ = z.enum(DRILL_PROGRAMS);

export const DrillShotClassZ = z.enum(['three', 'mid', 'rim', 'ft']);
export type DrillShotClass = z.infer<typeof DrillShotClassZ>;
export const SpotKeyZ = z.enum(SPOT_KEYS);

export const DrillSessionStatusZ = z.enum(['live', 'final', 'abandoned']);

export const DrillSessionZ = z.object({
  id: IdZ,
  program: DrillProgramZ,
  level: CombineLevelZ.nullable(),
  status: DrillSessionStatusZ,
  playerIds: z.array(IdZ),
  startedAt: TsZ,
  endedAt: TsZ.nullable(),
  createdBy: IdZ,
  sessionId: IdZ.nullable(),
  deletedAt: TsZ.nullable(),
});
export type DrillSession = z.infer<typeof DrillSessionZ>;

/** What the recorder sends (through the outbox, stream kind 'drill'). */
export const DrillAttemptZ = z.object({
  id: IdZ,
  drillSessionId: IdZ,
  playerId: IdZ,
  level: CombineLevelZ.nullable(),
  spot: SpotKeyZ.nullable(),
  shotClass: DrillShotClassZ,
  made: z.boolean(),
  /** Per device per drill session, from 1. */
  seq: z.number().int().positive(),
  deviceId: IdZ,
  clientTs: TsZ,
  authorMemberId: IdZ,
});
export type DrillAttempt = z.infer<typeof DrillAttemptZ>;

export const StoredDrillAttemptZ = DrillAttemptZ.extend({
  serverSeq: z.number().int().positive(),
  receivedAt: TsZ,
  voidedAt: TsZ.nullable(),
});
export type StoredDrillAttempt = z.infer<typeof StoredDrillAttemptZ>;

export const DrillSessionDetailZ = z.object({
  session: DrillSessionZ,
  attempts: z.array(StoredDrillAttemptZ),
  /** Iso Gauntlet games linked to this session. */
  gameIds: z.array(IdZ),
  deltas: z.array(RatingDeltaZ).nullable(),
});
export type DrillSessionDetail = z.infer<typeof DrillSessionDetailZ>;
